import { useCallback, useEffect, useRef, useState } from "react";
import { listClientNotices } from "@/features/notices/api";
import type { Notice } from "@/features/notices/types";
import { ApiError } from "@/lib/api";

interface UseClientNoticesResult {
  notices: Notice[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

/** Fetches a client's notices; same race guards as use-cases.ts. */
export function useClientNotices(clientId: string): UseClientNoticesResult {
  const [notices, setNotices] = useState<Notice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const lastKeyRef = useRef<string | null>(null);
  const requestIdRef = useRef(0);
  const mountedRef = useRef(true);

  const refetch = useCallback(() => setReloadKey((k) => k + 1), []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const key = `${reloadKey}|${clientId}`;
    if (lastKeyRef.current === key) return;
    lastKeyRef.current = key;

    const reqId = ++requestIdRef.current;
    setLoading(true);
    setError(null);

    listClientNotices(clientId)
      .then((result) => {
        if (!mountedRef.current || requestIdRef.current !== reqId) return;
        setNotices(result);
      })
      .catch((err) => {
        if (!mountedRef.current || requestIdRef.current !== reqId) return;
        setNotices([]);
        setError(err instanceof ApiError ? err.message : "Failed to load notices.");
      })
      .finally(() => {
        if (mountedRef.current && requestIdRef.current === reqId) setLoading(false);
      });
  }, [clientId, reloadKey]);

  return { notices, loading, error, refetch };
}
